import React from 'react';
import {
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Sparkles,
  HelpCircle,
} from 'lucide-react';
import { FormulaRouteResult, FormulaRouteChain, Question } from '../../types';
import { VARIABLE_BY_ID } from '../../data/formulas';
import { InlineLatex } from './LatexRenderer';
import { FormulaStepCard } from './FormulaStepCard';
import { FormulaUnblockingCard } from './FormulaUnblockingCard';
import { FormulaEmptyState } from './FormulaEmptyState';

interface FormulaDeductionViewProps {
  selectedKnowns: string[];
  selectedTarget: string;
  finderResult: FormulaRouteResult | null;
  activeChain: FormulaRouteChain | null;
  activeChainIndex: number;
  setActiveChainIndex: (idx: number) => void;
  questionsByFormulaId: Record<string, Question[]>;
  onAddKnown: (id: string) => void;
  onSetTarget: (id: string) => void;
}

export const FormulaDeductionView: React.FC<FormulaDeductionViewProps> = ({
  selectedKnowns,
  selectedTarget,
  finderResult,
  activeChain,
  activeChainIndex,
  setActiveChainIndex,
  questionsByFormulaId,
  onAddKnown,
  onSetTarget,
}) => {
  return (
    <div className="flex flex-1 flex-col h-full rounded-xl border border-paper-300 bg-paper-100 dark:border-study-line dark:bg-study-panel p-4 overflow-hidden shadow-sm">
      {/* Header Status & Multi-Chain Selector */}
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-b border-paper-200 pb-3 dark:border-study-line mb-3">
        <div className="flex items-center gap-2">
          {finderResult && finderResult.found ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
          ) : selectedTarget && selectedKnowns.length > 0 ? (
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
          ) : (
            <HelpCircle className="h-5 w-5 text-zinc-400 shrink-0" />
          )}

          <div>
            <h2 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              {finderResult && finderResult.found
                ? activeChain && activeChain.steps.length === 1
                  ? 'Fórmula Direta Encontrada'
                  : `Dedução em ${activeChain?.steps.length || 0} Etapas`
                : selectedTarget && selectedKnowns.length > 0
                ? 'Resolução Incompleta com os Dados Fornecidos'
                : !selectedTarget && selectedKnowns.length > 0
                ? 'Defina a Incógnita Alvo (🎯 Alvo)'
                : selectedTarget && selectedKnowns.length === 0
                ? 'Adicione Dados Conhecidos (+ Dado)'
                : 'Selecione Dados e Alvo para Iniciar a Dedução'}
            </h2>
            {finderResult && finderResult.found && selectedTarget ? (
              <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium">
                Cálculo exato de {VARIABLE_BY_ID[selectedTarget]?.name} (
                <InlineLatex>{VARIABLE_BY_ID[selectedTarget]?.symbol || ''}</InlineLatex>)
              </span>
            ) : (
              <span className="text-[11px] text-zinc-500 dark:text-zinc-400">
                {!selectedTarget && selectedKnowns.length > 0
                  ? `${selectedKnowns.length} dado(s) informado(s). Clique em 🎯 Alvo na grandeza que deseja encontrar.`
                  : selectedTarget && selectedKnowns.length === 0
                  ? `Alvo definido como ${VARIABLE_BY_ID[selectedTarget]?.name}. Adicione os valores conhecidos do problema.`
                  : 'Escolha as grandezas conhecidas do problema e o que você precisa calcular.'}
              </span>
            )}
          </div>
        </div>

        {/* Multiple minimal chains tie selector */}
        {finderResult && finderResult.chains.length > 1 && (
          <div className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 p-1 dark:border-emerald-800 dark:bg-emerald-950/40">
            <span className="text-[10px] uppercase font-bold text-emerald-800 dark:text-emerald-300 px-1">
              Rotas Equivalentes ({finderResult.chains.length}):
            </span>
            {finderResult.chains.map((c, idx) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveChainIndex(idx)}
                className={`rounded px-2 py-0.5 text-xs font-mono font-bold transition ${
                  activeChainIndex === idx
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-emerald-800 hover:bg-emerald-200 dark:text-emerald-300 dark:hover:bg-emerald-900'
                }`}
              >
                Caminho {idx + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable Deduction Content Body */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1.5">
        {/* 1. When route is found */}
        {finderResult && finderResult.found && activeChain && (
          <div className="space-y-4">
            {activeChain.steps.map((step, idx) => (
              <FormulaStepCard
                key={idx}
                step={step}
                stepIndex={idx}
                totalSteps={activeChain.steps.length}
                questions={questionsByFormulaId[step.equation.id] || []}
              />
            ))}

            {/* Reachable Alternatives (Breadth-First Exploration) */}
            {finderResult.otherReachable && finderResult.otherReachable.length > 0 && (
              <div className="rounded-xl border border-paper-300 bg-paper-50 p-4 dark:border-study-line dark:bg-study-card space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 dark:text-emerald-300">
                  <Sparkles className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span>Com esses mesmos dados conhecidos, você também calcula diretamente:</span>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {finderResult.otherReachable.map(({ variable }) => (
                    <button
                      key={variable.id}
                      type="button"
                      onClick={() => onSetTarget(variable.id)}
                      className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-paper-100 px-3 py-1.5 text-xs font-medium text-emerald-950 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-study-panel dark:text-emerald-200 dark:hover:bg-emerald-950 transition"
                    >
                      <span className="font-mono font-bold">
                        <InlineLatex>{variable.symbol}</InlineLatex>
                      </span>
                      <span>{variable.name}</span>
                      <ArrowRight className="h-3 w-3 text-emerald-600 dark:text-emerald-400 ml-1" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 2. When route is NOT reachable with current data */}
        {finderResult && !finderResult.found && selectedTarget && (
          <FormulaUnblockingCard
            selectedKnowns={selectedKnowns}
            selectedTarget={selectedTarget}
            unblockingGrandezas={finderResult.unblockingGrandezas}
            onAddKnown={onAddKnown}
          />
        )}

        {/* 3. Empty State (No target or no knowns) */}
        {(!finderResult || !selectedTarget || selectedKnowns.length === 0) && (
          <FormulaEmptyState
            selectedKnownsCount={selectedKnowns.length}
            selectedTarget={selectedTarget}
          />
        )}
      </div>
    </div>
  );
};
